import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { UserNotificationResponse } from '@1po/1po-bff-fe-spec/generated/user/response/UserNotificationResponse';
import { notification as antdNotification } from 'antd-v5';
import { mapNotificationToType } from 'components/UserNotifications/notificationsUtils';
import {
  cancelNotificationRequest,
  getUserNotifications,
  getUserRights,
  ignoreAllNotificationsRequest,
  ignoreNotificationRequest,
} from 'domains/user';
import { BlueButton, Flex, Text, WhiteButton } from 'UI';
import { getData, getSearchData } from 'utils';

const MAX_POPUP_NOTIFICATIONS = 50;

const NotificationsPopup = () => {
  const { t } = useTranslation();
  const [api, contextHolder] = antdNotification.useNotification();
  const [shownUnreadNotifications, setShownUnreadNotifications] = useState<string[]>([]);
  const notifications = useSelector(getUserNotifications);
  const userRoles = getData(useSelector(getUserRights)) ?? [];
  const history = useHistory();
  const dispatch = useDispatch();

  const openNotification = (notification: UserNotificationResponse) => {
    const handleLinkClick = () => {
      api.destroy(key);
      if (notification.notificationId) {
        dispatch(ignoreNotificationRequest({ notificationId: notification.notificationId }));
      }
    };
    const notificationData = mapNotificationToType(t, notification, userRoles, handleLinkClick);
    const key = notification.notificationId ?? '';

    const handleRedirectFromNotification = () => {
      if (notificationData?.link && notification.notificationId) {
        api.destroy(key);
        history.push(notificationData.link);
        dispatch(cancelNotificationRequest({ notificationId: notification.notificationId }));
      }
    };

    const handleIgnoreAll = () => {
      api.destroy();
      dispatch(ignoreAllNotificationsRequest());
    };

    const handleIgnoreSingle = () => {
      if (notification.notificationId) {
        dispatch(ignoreNotificationRequest({ notificationId: notification.notificationId }));
      }
    };

    const btn = (
      <Flex direction={'row-reverse'} gap={5}>
        <BlueButton
          onClick={handleRedirectFromNotification}
          disabled={notificationData?.disableLink}
          data-cy={'notification-popin-see'}
        >
          {t('notifications.action.see', 'See')}
        </BlueButton>
        <WhiteButton onClick={handleIgnoreAll} data-cy={'notification-popin-ignoreall'}>
          {t('common.ignore_all', 'Ignore all')}
        </WhiteButton>
      </Flex>
    );
    api.open({
      key,
      icon: notificationData?.icon,
      message: (
        <Text data-cy={'notification-popin-message'} type="base" bold>
          {notificationData?.title ?? ''}
        </Text>
      ),
      description: (
        <Text data-cy={'notification-popin-description'} type="base">
          {notificationData?.description ?? ''}
        </Text>
      ),
      btn,
      duration: 0,
      style: { width: 400 },
      onClose: handleIgnoreSingle,
    });
    setShownUnreadNotifications((prev) => [...prev, key]);
  };

  useEffect(() => {
    const notificationsData = getSearchData(notifications)?.notifications ?? [];
    const notificationsToPopup = notificationsData.filter((notif) => !notif.ignored);

    notificationsToPopup
      .slice(0, MAX_POPUP_NOTIFICATIONS)
      .reverse()
      .forEach((notification) => {
        if (notification.notificationId && !shownUnreadNotifications.includes(notification.notificationId)) {
          openNotification(notification);
        }
      });
    //eslint-disable-next-line
  }, [notifications.data?.notifications]);

  return <>{contextHolder}</>;
};

export default NotificationsPopup;
